import type { Product, ProductVariant } from './types';
import { readProductOptions } from './types';

export interface CouleurImage {
	/** Libellé tel qu'il est écrit dans l'axe d'option : « Bleu nuit ». */
	label: string;
	/** Code hexadécimal, quand la valeur d'option en porte un. */
	hex?: string;
}

/**
 * Apparence d'une déclinaison : sa couleur et, si elle en a une, son image.
 *
 * Un visuel propre à la déclinaison prime sur celui porté par la valeur
 * d'option — c'est le cas le plus précis, même s'il reste rare dans le
 * catalogue actuel.
 */
export function apparenceVariante(
	produit: Product,
	variante: ProductVariant,
): { hex?: string; imageUrl?: string; axeCouleur?: string } {
	const axe = readProductOptions(produit.attributes).find((o) => o.type === 'color');
	if (!axe) return {};

	const choisie = variante.optionValues?.[axe.name];
	const valeur = axe.values.find((v) => v.label === choisie);
	if (!valeur) return { axeCouleur: axe.name };

	const propre = (variante.mediaLinks ?? []).find((m) => m.url)?.url ?? undefined;
	const parOption = valeur.mediaId
		? ((produit.mediaLinks ?? []).find((m) => m.mediaId === valeur.mediaId)?.url ?? undefined)
		: undefined;

	return { hex: valeur.color, imageUrl: propre ?? parOption, axeCouleur: axe.name };
}

/**
 * Lecture inverse : pour chaque média du produit, les couleurs qu'il illustre.
 *
 * En revue, on regarde la photo avant de lire le texte ; savoir que la
 * troisième vignette est le modèle rouge évite d'ouvrir la fiche complète pour
 * vérifier qu'une déclinaison n'a pas hérité du visuel d'une autre.
 *
 * Une même image peut servir plusieurs couleurs (un lot, un plan de détail) :
 * la valeur est donc une liste, pas une couleur unique.
 */
export function couleursParMedia(produit: Product): Map<string, CouleurImage[]> {
	const carte = new Map<string, CouleurImage[]>();
	const ajouter = (mediaId: string, c: CouleurImage) => {
		const deja = carte.get(mediaId);
		if (!deja) carte.set(mediaId, [c]);
		else if (!deja.some((x) => x.label === c.label)) deja.push(c);
	};

	const axe = readProductOptions(produit.attributes).find((o) => o.type === 'color');
	if (axe) {
		for (const v of axe.values) {
			if (v.mediaId) ajouter(v.mediaId, { label: v.label, hex: v.color });
		}
	}

	// Une déclinaison peut aussi porter directement son visuel.
	for (const variante of produit.variants ?? []) {
		const couleur = axe ? variante.optionValues?.[axe.name] : undefined;
		if (!couleur) continue;
		const hex = axe?.values.find((v) => v.label === couleur)?.color;
		for (const m of variante.mediaLinks ?? []) {
			if (m.mediaId) ajouter(m.mediaId, { label: couleur, hex });
		}
	}

	return carte;
}
